import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { FilterableCheckboxListItem } from '../../../../components/filterable-checkbox-list/use-filterable-checkbox-list';
import { CollapsibleFilterList } from '../../../appointment-list-filter-button/filter-drawer/collapsible-filter-list';
import { FilterState } from './filters-drawer';

export const AppliedFilters = ({
  normalizedValues,
  filterLists,
  onRemoveFilter,
}: {
  normalizedValues: FilterState;
  filterLists: {
    providers: FilterableCheckboxListItem[];
    operatories: FilterableCheckboxListItem[];
    services: FilterableCheckboxListItem[];
  };
  onRemoveFilter: (type: string, value: string) => void;
}) => {
  const { t } = useTranslation('schedule-settings');

  const appliedFilters = () => {
    const items = [];

    normalizedValues.providers.forEach((id) => {
      const label = filterLists.providers.find((p) => p.value === id)?.label || id;
      items.push({ type: 'provider', value: id, label });
    });

    normalizedValues.operatories.forEach((id) => {
      const label = filterLists.operatories.find((o) => o.value === id)?.label || id;
      items.push({ type: 'operatory', value: id, label });
    });

    normalizedValues.serviceTypes.forEach((id) => {
      const label = filterLists.services.find((s) => s.value === id)?.label || id;
      items.push({ type: 'service', value: id, label });
    });

    if (normalizedValues.startDate) {
      items.push({
        type: 'startDate',
        value: normalizedValues.startDate,
        label: `Start: ${normalizedValues.startDate}`,
      });
    }
    if (normalizedValues.endDate) {
      items.push({ type: 'endDate', value: normalizedValues.endDate, label: `End: ${normalizedValues.endDate}` });
    }

    if (normalizedValues.view && normalizedValues.view !== 'CURRENT') {
      items.push({ type: 'view', value: normalizedValues.view, label: normalizedValues.view });
    }

    const recurrenceLabels: Record<FilterState['recurrenceTypes'][number], string> = {
      SINGLE_OCCURRENCE: t('Single'),
      WEEKLY: t('Weekly'),
      CUSTOM: t('Custom'),
      SPECIFIC_DATE: t('Specific Date'),
    };

    normalizedValues.recurrenceTypes.forEach((recurrenceType) => {
      items.push({
        type: 'recurrence',
        value: recurrenceType,
        label: recurrenceLabels[recurrenceType] || recurrenceType,
      });
    });

    return items;
  };

  if (!appliedFilters().length) return null;

  return (
    <div css={appliedFiltersContainerStyles}>
      <Text size='small' weight='bold'>
        {t('Applied Filters ({{count}})', { count: appliedFilters().length })}
      </Text>
      {appliedFilters().length > 0 ? (
        <CollapsibleFilterList selectedFilters={appliedFilters()} onRemove={onRemoveFilter} maxRows={2} />
      ) : (
        <Text size='small'>{t('No filters applied')}</Text>
      )}
    </div>
  );
};

const appliedFiltersContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 0, 0, 0),
  borderTop: `1px solid ${theme.colors.neutral20}`,
  backgroundColor: theme.colors.white,
  zIndex: theme.zIndex.low,
});
